import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {SampleDetails} from 'constants/SampleConstants';
import {
  NO_DESTINATIONS_AVAILABLE,
  NO_SAMPLES_AVAILABLE_FOR_THIS_DESTINATION,
} from 'notebook/sample-propagation/add-samples-modal//AddSamplesModalConstants';
import ColumnSelector from 'notebook/sample-propagation/add-samples-modal//ColumnSelector';
import SourceTableSelector from 'notebook/sample-propagation/add-samples-modal//SourceTableSelector';
import {
  AddSamplesFromStructuredTableSourceAnalyticsInfo,
  DestinationSampleFiltersAndColumns,
} from 'notebook/sample-propagation/add-samples-modal/AddSamplesFromStructuredTableModal';
import {AddFromStructuredTablePasteOption} from 'notebook/sample-propagation/AddFromStructuredTableConstants';
import {SampleWithRowCount} from 'notebook/sample-propagation/AddSamplesFromWorklistModal';
import {SamplesSource} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import SourceSampleSelectionTable from 'notebook/sample-propagation/sample-selection/SourceSampleSelectionTable';
import {SampleFilter, sampleFilterToIcon} from 'notebook/sample-propagation/SampleFilter';
import {checkIfSamplesDetailsHaveDuplicates} from 'notebook/sample-propagation/util/dedupeSampleDetails';
import filterSamplesSources from 'notebook/sample-propagation/util/filterSamplesSources';
import {SampleDescriptor} from 'notebook/sample-propagation/util/getSampleDescriptor';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';

const ADD_FROM_STRUCTURED_TABLE_PASTE_OPTIONS = [
  {label: 'Append', value: AddFromStructuredTablePasteOption.APPEND},
  {
    label: 'Replace',
    value: AddFromStructuredTablePasteOption.REPLACE,
  },
];

export interface AddSamplesModalFormControlsProps {
  // See the notes on props in AddSamplesFromStructuredTableModal
  samplesSources: Array<SamplesSource>;
  destinationSampleFiltersByColumn?: Array<DestinationSampleFiltersAndColumns>;
  initialDestinationColumnIndex?: number;
  destinationSampleType?: SampleFilter | null;
  sampleDescriptor: SampleDescriptor;
  onChangeDestinationColumnIndex: (index: number) => void;
  onChangeSelectedSamples: (samples: Array<SampleDetails>) => void;
  pasteOption: AddFromStructuredTablePasteOption;
  onChangePasteOption: (pasteOption: AddFromStructuredTablePasteOption) => void;
  removeDuplicates: boolean;
  onChangeRemoveDuplicates: (removeDuplicates: boolean) => void;

  // For analytics only
  onChangeSourceTableOrColumn: ({
    table,
    column,
    columnDefinition,
  }: AddSamplesFromStructuredTableSourceAnalyticsInfo) => void;
  // Returns 0 if there are no samples (therefore no replication factors), otherwise a positive integer
  onChangeLargestReplicationFactor: (largestReplicationFactor: number) => void;
}

export default function AddSamplesModalFormControls(props: AddSamplesModalFormControlsProps): JSX.Element {
  const {
    samplesSources,
    onChangeDestinationColumnIndex,
    onChangeSelectedSamples,
    destinationSampleFiltersByColumn,
    onChangeSourceTableOrColumn,
    onChangeLargestReplicationFactor,
  } = props;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeDestinationColumnIndexCallback = useCallback(onChangeDestinationColumnIndex, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeSelectedSamplesCallback = useCallback(onChangeSelectedSamples, []);
  const shouldSuppressDestinationSelector = destinationSampleFiltersByColumn == null;
  // Note the destination column index is an index into the `destinationSampleFiltersByColumn` array,
  // which is a subset of the table's actual column list

  const [destinationColumnIndex, setDestinationColumnIndex] = useState<number | null>(
    shouldSuppressDestinationSelector
      ? null
      : destinationSampleFiltersByColumn.length === 1
        ? 0
        : (props.initialDestinationColumnIndex ?? -1)
  );

  // Filtered sources that contain a column that can go into the destination column
  // Note: the columns within each of these sources is filtered to only potential source columns that match the destination column.
  const acceptableSampleSources = useMemo(() => {
    const dest: DestinationSampleFiltersAndColumns | undefined =
      destinationSampleFiltersByColumn?.[destinationColumnIndex!];

    // Get single destination filter using sampleFilter
    const destinationSampleFilter: SampleFilter | null =
      dest?.sampleFilter ?? props.destinationSampleType ?? null;

    // Get multiple destination filters using sampleFilters (gated feature), or fallback to destinationSampleFilter
    const destinationSampleFilters: Array<SampleFilter> | null =
      dest?.sampleFilters ?? (destinationSampleFilter ? [destinationSampleFilter] : null);

    return filterSamplesSources(samplesSources, destinationSampleFilters);
  }, [destinationSampleFiltersByColumn, destinationColumnIndex, props.destinationSampleType, samplesSources]);

  const [sourceTableIndex, setSourceTableIndex] = useState<number>(
    // Setting the default source table: if only one table is provided, and the previous dropdown in
    // the modal (destination column) has been used or is suppressed, we can pre-select the first
    // entry in the list. Otherwise, force the user to make a choice.
    acceptableSampleSources.length === 1 &&
      (shouldSuppressDestinationSelector || destinationSampleFiltersByColumn.length === 1)
      ? 0
      : -1
  );
  const [sourceTableColumnIndex, setSourceTableColumnIndex] = useState<number>(
    sourceTableIndex === 0 ? 0 : -1
  );

  // This tracks the list of samples available for (not "selected for") transfer. No row counts, just the full list.
  // Returns the full list of unique samples in the currently selected source table/column.
  const allSamplesInSourceColumn: Array<SampleDetails> = useMemo(
    () => acceptableSampleSources[sourceTableIndex]?.samplesByColumn[sourceTableColumnIndex]?.samples ?? [],
    [acceptableSampleSources, sourceTableColumnIndex, sourceTableIndex]
  );

  const doSamplesHaveDuplicates: boolean = useMemo(
    () => checkIfSamplesDetailsHaveDuplicates(allSamplesInSourceColumn),
    [allSamplesInSourceColumn]
  );

  // This tracks the selected samples with their row counts. Error state (null) is not yet handled.
  const [selectedSamplesWithRowCounts, setSamplesWithRowCounts] = useState<Array<SampleWithRowCount> | null>(
    []
  );

  const resetSourceTableSelector: () => void = useCallback(() => {
    setSourceTableIndex(
      (shouldSuppressDestinationSelector || destinationColumnIndex! >= 0) &&
        acceptableSampleSources.length === 1
        ? 0
        : -1
    );
  }, [shouldSuppressDestinationSelector, destinationColumnIndex, acceptableSampleSources]);

  // When the destination column changes, reset the source table, and alert the caller
  useEffect(() => {
    resetSourceTableSelector();
    if (!shouldSuppressDestinationSelector) {
      onChangeDestinationColumnIndexCallback(destinationColumnIndex!);
    }
  }, [
    shouldSuppressDestinationSelector,
    destinationColumnIndex,
    onChangeDestinationColumnIndexCallback,
    resetSourceTableSelector,
  ]);

  // When the destination changes, clear out the selected samples. If the SourceSampleSelectionTable
  // is rendered (which is a maybe), the table will fill the selected samples with all of the ones it knows
  // about.
  //
  // If the table is NOT rendered (because there are no samples that match the selected destination),
  // we need to ensure that the caller knows that there are no samples available for propagation.
  useEffect(() => {
    onChangeSelectedSamplesCallback([]);
  }, [destinationColumnIndex, onChangeSelectedSamplesCallback]);

  // When the list of acceptable sources changes, update the source table index
  useEffect(resetSourceTableSelector, [acceptableSampleSources, resetSourceTableSelector]);

  // When the source table changes, reset the source column
  useEffect(() => {
    setSourceTableColumnIndex(
      acceptableSampleSources[sourceTableIndex]?.samplesByColumn.length === 1 ? 0 : -1
    );
  }, [sourceTableIndex, acceptableSampleSources]);

  // When the selected samples change, alert the caller
  useEffect(() => {
    // Expanded version of `selectedSamplesWithRowCounts`, a list of (non-unique) samples
    const selectedSamples =
      selectedSamplesWithRowCounts?.flatMap(
        ({sample, rowCount}): Array<SampleDetails> => Array(rowCount).fill(sample)
      ) ?? [];
    onChangeSelectedSamplesCallback(selectedSamples);
  }, [onChangeSelectedSamplesCallback, selectedSamplesWithRowCounts]);

  // Alert the caller when the source table or source column changes, for analytics
  const source = acceptableSampleSources[sourceTableIndex];
  const sourceColumnSamples = source?.samplesByColumn[sourceTableColumnIndex];
  useEffect(() => {
    onChangeSourceTableOrColumn({
      table: source?.table ?? null,
      column: sourceColumnSamples?.column ?? null,
      columnDefinition: sourceColumnSamples?.columnDefinition ?? null,
    });
  }, [
    onChangeSourceTableOrColumn,
    source?.table,
    sourceColumnSamples?.column,
    sourceColumnSamples?.columnDefinition,
  ]);
  const largestReplicationFactor = useMemo(
    () =>
      selectedSamplesWithRowCounts?.length
        ? Math.max(...selectedSamplesWithRowCounts.map(({rowCount}) => rowCount))
        : 0,
    [selectedSamplesWithRowCounts]
  );
  useEffect(() => {
    onChangeLargestReplicationFactor(largestReplicationFactor);
  }, [onChangeLargestReplicationFactor, largestReplicationFactor]);

  return destinationSampleFiltersByColumn?.length === 0 ? (
    <p>{NO_DESTINATIONS_AVAILABLE}</p>
  ) : (
    <>
      {!shouldSuppressDestinationSelector && (
        <>
          <strong className={css(styles.columnSelectorHeading)}>Paste to</strong>
          <ColumnSelector
            label="Destination column"
            columns={destinationSampleFiltersByColumn.map((destinationInfo) => ({
              name: destinationInfo.destinationDisplayName,
              icon: sampleFilterToIcon(destinationInfo.sampleFilter ?? props.destinationSampleType),
            }))}
            onChange={setDestinationColumnIndex}
            selectedColumnIndex={destinationColumnIndex!}
          />
        </>
      )}
      {acceptableSampleSources.length === 0 ? (
        <p className="u-spacing-top">{NO_SAMPLES_AVAILABLE_FOR_THIS_DESTINATION}</p>
      ) : (
        <>
          <strong className={classNames(css(styles.columnSelectorHeading), 'u-spacing-top')}>
            Copy from
          </strong>
          <div className="u-flex-display">
            <div className="u-flex-full-height u-small-spacing-right u-spacing-bottom">
              <SourceTableSelector
                disabled={destinationSampleFiltersByColumn && destinationColumnIndex === -1}
                selectedTableIndex={sourceTableIndex}
                sources={acceptableSampleSources}
                onChange={setSourceTableIndex}
              />
            </div>
            <div className="u-flex-full-height">
              <ColumnSelector
                label="Source column"
                disabled={sourceTableIndex === -1}
                columns={(acceptableSampleSources[sourceTableIndex]?.samplesByColumn ?? []).map((column) => ({
                  name: column.displayName,
                  icon: sampleFilterToIcon(column.sampleFilter),
                }))}
                onChange={setSourceTableColumnIndex}
                selectedColumnIndex={sourceTableColumnIndex}
              />
            </div>
          </div>
          {allSamplesInSourceColumn.length > 0 && (
            <>
              <RadioButtonGroup
                title="Paste Option"
                options={ADD_FROM_STRUCTURED_TABLE_PASTE_OPTIONS}
                selectedValue={props.pasteOption}
                onChange={props.onChangePasteOption}
              />

              <div className="u-spacing-bottom">
                <CheckboxInput
                  id="set-remove-duplicates"
                  label="Remove duplicates"
                  // Disable checkbox and keep unchecked if there are no duplicates
                  checked={props.removeDuplicates && doSamplesHaveDuplicates}
                  disabled={!doSamplesHaveDuplicates}
                  onChange={(e) => {
                    props.onChangeRemoveDuplicates(e.target.checked);
                  }}
                />
              </div>
            </>
          )}
          <SourceSampleSelectionTable
            samples={allSamplesInSourceColumn}
            onChangeSelection={setSamplesWithRowCounts}
            sampleDescriptor={props.sampleDescriptor}
            sourceKey={`${acceptableSampleSources[sourceTableIndex]?.name ?? 'unknown'}~${
              acceptableSampleSources[sourceTableIndex]?.samplesByColumn[sourceTableColumnIndex]
                ?.displayName ?? 'unknown'
            }`}
            sourceName={
              acceptableSampleSources[sourceTableIndex]?.samplesByColumn[sourceTableColumnIndex]?.displayName
            }
            removeDuplicates={props.removeDuplicates}
          />
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  columnSelectorHeading: {
    display: 'block',
    fontSize: 14,
    marginBottom: -8,
  },
});
